import { FilterSectionData } from "@shared/features/filters/types";
import RangePicker from "common/DatePicker/RangePicker";
import React, { CSSProperties } from "react";
import { dateToDayjs } from "@shared/utils/date";
import styled from "styled-components";
import { FilterType } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import type { Dayjs } from "dayjs";
import { useBottomNavigator } from "utils/hooks/misc";
import { MobileResetButton } from "common/Filters/common/styles";

interface TimeRangeFiltersSectionProps
  extends FilterSectionData<FilterType.TIME_FRAME> {
  style?: CSSProperties;
}

const TimeRangeFiltersSection = ({
  options,
  value,
  onChange,
  style,
  isSingleRowView,
  isChipsView,
  resetButtonData,
  allSelected,
}: TimeRangeFiltersSectionProps) => {
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  const onDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onChange({
        range: [dates[0].startOf("day").unix(), dates[1].endOf("day").unix()],
      });
    } else {
      onChange({ range: options });
    }
  };

  const getValue = (): [Dayjs, Dayjs] | undefined => {
    const { range } = value;
    if (range[0] && range[1])
      return [dateToDayjs(range[0], true), dateToDayjs(range[1], true)];
  };

  const onPresetsClick = (value: [Dayjs | null, Dayjs | null]) => {
    if (value[0] && value[1]) {
      onChange({
        range: [value[0]?.unix(), value[1]?.unix()],
      });
    }
  };

  return (
    <Wrapper
      $isSingleRowView={isSingleRowView}
      $isBottomNavigator={isBottomNavigator}
    >
      <StyledRangePicker
        value={getValue()}
        onChange={onDateChange}
        style={style}
        localRender={!!isChipsView || isMobile}
        $isSingleRowView={isSingleRowView}
        onPresetsClick={onPresetsClick}
        bottomExtraElement={
          isBottomNavigator && isSingleRowView ? (
            <MobileResetButton
              onClick={resetButtonData.onClick}
              disabled={allSelected}
            >
              {resetButtonData.resetText}
            </MobileResetButton>
          ) : undefined
        }
        $hasValue={!!value.range.length}
        $showClear={!isBottomNavigator}
        $isBottomNavigator={isBottomNavigator}
      />
    </Wrapper>
  );
};

export default TimeRangeFiltersSection;

const Wrapper = styled.div<{
  $isSingleRowView?: boolean;
  $isBottomNavigator?: boolean;
}>`
  ${(p) =>
    p.$isSingleRowView &&
    `
    width: 232px;
  `}
  ${(p) =>
    p.$isBottomNavigator &&
    `
    .ant-picker-dropdown {
      top: 20px !important;
    }
  `}
`;
const StyledRangePicker = styled(RangePicker)<{
  $isSingleRowView?: boolean;
  $hasValue?: boolean;
  $showClear?: boolean;
  $isBottomNavigator?: boolean;
}>`
  ${(p) =>
    p.$isSingleRowView &&
    `
  &.ant-picker {
    background: ${
      p.$hasValue && p.$isBottomNavigator
        ? p.theme.colors.filterChipsActiveColor
        : p.theme.colors.filterChipsColor
    } !important;
    border: none;
    box-shadow: none;
    border-radius: 50px;
    height: 32px;
  }
  &.ant-picker-range .ant-picker-range-separator {
    padding: 0 4px !important;
  }
  &.ant-picker .ant-picker-input > input {
    font-size: 13px;
    text-align: center;
    color: ${
      p.$hasValue && p.$isBottomNavigator
        ? p.theme.colors.white
        : p.theme.colors.primaryGrey
    };
  }
  &.ant-picker .ant-picker-input > input::placeholder {
    color: ${p.theme.colors.primaryGrey};
    text-align: center;
  }
  .anticon-swap-right > svg, .anticon-calendar > svg {
    fill: ${
      p.$hasValue && p.$isBottomNavigator
        ? p.theme.colors.white
        : p.theme.colors.primaryGrey
    };
  }
  .ant-picker-clear {
    background: ${p.theme.colors.filterChipsColor} !important;
    color: ${p.theme.colors.lightestGrey} !important;
  }
  .ant-picker-dropdown .ant-picker-footer-extra {
    line-height: 32px !important;
  }`};
  ${(p) =>
    !p.$showClear &&
    `
  .ant-picker-clear {
    opacity: 0 !important;
  }
  .ant-picker-suffix:not(:last-child) {
    opacity: 1 !important;
  }
  
  `};
`;
